import React, { useEffect } from 'react';
import { useSetRecoilState, useRecoilState } from 'recoil';
import { useLocation } from 'react-router-dom';
import Cookie from 'js-cookie';
import URLSearchParams from '@ungap/url-search-params';
import { useLazyQuery } from '@apollo/client';
import { Modal } from '@Calix-Commerce/design-system/components';

import { authenticationState, loadingState } from 'state/atoms';
import { storageCurrentAccountSelector } from 'state/selectors';
import {
  ORDER_MANAGER_PERMISSION,
  RMA_USER_PERMISSION,
  RMA_MANAGER_PERMISSION,
  USER_REQUEST_PATH_COOKIE,
  ACCOUNT_ID_PARAM,
} from 'utils/constants';
import { mapAccountDetailsToStorage } from 'utils/helpers/account';
import { getAccountsUrlWithReturn } from 'utils/helpers/url';
import { CALIX_EMPLOYEE, VAR } from 'connectors/userTypes';
import { GET_ACCOUNT } from 'connectors/queries/account';
import { getAuthFromStorage } from 'connectors/storage';

import { useAccountLoad } from './useAccountLoad';
import { notAuthenticatedConfig, emptyAccessConfig, noAccessConfig } from './modalConfigs';
import { clearLocalStorageData } from './helpers';
import { useSessionController } from './useSessionController';

const getModalConfig = ({ isAuthenticated, eCommerceAccess, commerceComplete }) => {
  if (!isAuthenticated) {
    return notAuthenticatedConfig;
  } else if (!eCommerceAccess.length || !commerceComplete) {
    return emptyAccessConfig;
  } else {
    return noAccessConfig;
  }
};

const redirectToAccountSearch = () => window.location.replace(getAccountsUrlWithReturn());

const AuthOverlay = ({ children }) => {
  const setLoading = useSetRecoilState(loadingState);
  const setAccount = useSetRecoilState(storageCurrentAccountSelector);
  const [authentication, setAuthentication] = useRecoilState(authenticationState);

  useSessionController();

  const { isAuthenticated, eCommerceAccess, userType, endCustomerAccountId } = authentication;

  const { pathname, search } = useLocation();
  const urlAccountIdParam = new URLSearchParams(search).get(ACCOUNT_ID_PARAM);

  const isVarOrManager = userType === VAR || userType === CALIX_EMPLOYEE;
  const isUserSwitchingAccount =
    isVarOrManager && urlAccountIdParam && urlAccountIdParam !== endCustomerAccountId;

  const [getAccount, switchAccountRequest] = useLazyQuery(GET_ACCOUNT, {
    onCompleted: (data) => {
      const newAccountData = mapAccountDetailsToStorage(data);
      setAccount(newAccountData);
      setAuthentication(getAuthFromStorage());
      clearLocalStorageData(newAccountData, authentication);
    },
    onError: redirectToAccountSearch,
  });

  const [{ commerceComplete }, { baseAccountRequest, endAccountRequest }] = useAccountLoad({
    skip: isUserSwitchingAccount, //Skip account loading if the user is switching accounts
  });

  const isLoadingAccount =
    switchAccountRequest.loading || baseAccountRequest.loading || endAccountRequest.loading;

  useEffect(() => {
    if (isLoadingAccount) {
      setLoading(true);
    }
  }, [setLoading, isLoadingAccount]);

  useEffect(() => {
    Cookie.remove(USER_REQUEST_PATH_COOKIE, { domain: '.calix.com' });

    if (isUserSwitchingAccount) {
      getAccount({ variables: { accountId: urlAccountIdParam } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasOrderManagerAccess =
    isAuthenticated && eCommerceAccess.indexOf(ORDER_MANAGER_PERMISSION) !== -1;

  const hasRmaAccess =
    isAuthenticated &&
    (eCommerceAccess.indexOf(RMA_MANAGER_PERMISSION) !== -1 ||
      eCommerceAccess.indexOf(RMA_USER_PERMISSION) !== -1);

  const isOrderDetailsPage = pathname.match(/\/orders\/\w+$/g);

  /**
    Redirects a VAR / Manager user without account selected to account search,
    unless it is Order Details Page
  */
  if (isVarOrManager && !isUserSwitchingAccount && !endCustomerAccountId && !isOrderDetailsPage) {
    return redirectToAccountSearch();
  }

  const canAccessPage = isOrderDetailsPage
    ? hasOrderManagerAccess || hasRmaAccess
    : hasOrderManagerAccess;

  // Makes sure that, in case of an account switch, the request has finished successfully
  const hasFinishedAccountSwitch = !isUserSwitchingAccount || switchAccountRequest.data;

  const canRenderApp = !isLoadingAccount && hasFinishedAccountSwitch;

  const renderModal = () => {
    setLoading(false); // Disable the global loading state when rendering the modal

    return (
      <Modal isOpen position="top" {...getModalConfig({ ...authentication, commerceComplete })} />
    );
  };

  const renderApp = () => (canAccessPage && commerceComplete ? children : renderModal());

  // Skip rendering the app if still loading account data
  return canRenderApp ? renderApp() : null;
};

export { AuthOverlay };
